/**
 * Thermal-printer view for a Rescigo invoice.
 *
 * Route: /finance/thermal/:id
 * Sized for 58 mm / 80 mm Bluetooth thermal printers. Uses a mm-based
 * print stylesheet and auto-fires window.print() once the invoice loads.
 *
 * Design constraints:
 *   • Monospace font, big totals, minimal chrome
 *   • Every dynamic line wraps cleanly at 32 chars (58 mm) or 42 chars (80 mm)
 *   • QR code is 40x40 mm — optional, hidden on 58 mm unless data-force
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Printer, X } from "@phosphor-icons/react";

const CSS = `
@page { margin: 4mm 3mm; size: 80mm auto; }
@media screen {
  body.thermal-body { background:#efeee8; padding:24px; }
  .paper { background:#fff; box-shadow:0 8px 24px rgba(0,0,0,0.06); }
}
.paper { width: 80mm; padding: 4mm 3mm; font-family: "Courier New", ui-monospace, monospace; font-size: 11px; line-height: 1.35; color:#000; }
.paper.mm58 { width: 58mm; font-size: 10.5px; }
.paper h1 { font-size: 15px; text-align:center; margin: 0 0 2mm; font-weight:900; letter-spacing:0.5px; }
.paper .sub { text-align:center; font-size: 10px; margin-bottom: 3mm; }
.paper hr { border: 0; border-top: 1px dashed #000; margin: 2mm 0; }
.paper .row { display:flex; justify-content:space-between; gap:2mm; }
.paper .label { color:#333; }
.paper .value { text-align:right; font-weight:700; }
.paper .bigtotal { font-size: 14px; font-weight:900; }
.paper .center { text-align:center; }
.paper .thanks { text-align:center; margin-top: 3mm; font-weight:700; }
@media print {
  html, body { margin: 0; padding: 0; }
  .no-print { display: none !important; }
  .paper { box-shadow: none; }
}
`;

export default function InvoiceThermal() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const size = sp.get("size") === "58" ? "58" : "80";  // default 80 mm
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/finance/invoices/${id}`);
      setInv(r.data);
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!inv) return;
    document.body.classList.add("thermal-body");
    return () => document.body.classList.remove("thermal-body");
  }, [inv]);

  // Auto-print once content is ready
  useEffect(() => {
    if (inv) setTimeout(() => window.print(), 500);
  }, [inv]);

  if (err) return <div style={{ padding: 32 }}>Could not load invoice: {err}</div>;
  if (!inv) return <div style={{ padding: 32 }}>Loading…</div>;

  const fmt = (n) => `Rs.${Number(n || 0).toFixed(2)}`;
  // Real Rescigo invoices use `label` for the description and `qty` for the
  // amount. Fall back to older synthetic shapes (description/name, quantity)
  // so hand-crafted test invoices still render.
  const nameOf = (it) => it.label || it.description || it.name || it.item_name || "Item";
  const qtyOf = (it) => it.qty !== undefined ? it.qty : (it.quantity !== undefined ? it.quantity : "");
  const dt = inv.date || (inv.created_at || "").slice(0, 10);
  const items = inv.items || [];
  const totalWaste = items
    .filter(i => (i.category || "").includes("BMW") || (i.category || "").includes("bio") || i.qty_kg)
    .reduce((s, i) => s + Number(i.qty_kg || i.quantity || 0), 0);

  return (
    <>
      <style>{CSS}</style>
      <div className="no-print" style={{ position: "fixed", top: 12, right: 12, display: "flex", gap: 8 }}>
        <button onClick={() => window.print()}
                data-testid="thermal-print-btn"
                style={{ background: "#2D5A27", color: "#fff", border: 0, padding: "8px 14px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "system-ui" }}>
          <Printer size={16}/> Print
        </button>
        <a href={`/finance/thermal/${id}?size=${size === "58" ? "80" : "58"}`}
           style={{ background: "#fff", color: "#333", border: "1px solid #ccc", padding: "8px 14px", borderRadius: 6, textDecoration: "none", fontFamily: "system-ui" }}>
          Switch to {size === "58" ? "80" : "58"} mm
        </a>
        <button onClick={() => window.close()}
                style={{ background: "#fff", color: "#333", border: "1px solid #ccc", padding: "8px 14px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "system-ui" }}>
          <X size={14}/> Close
        </button>
      </div>

      <div className={`paper ${size === "58" ? "mm58" : ""}`} data-testid="thermal-paper">
        <h1>RESCIGO HEALTH</h1>
        <div className="sub">Waste Management &amp; Compliance</div>
        <hr/>
        <div className="row"><span className="label">Invoice</span><span className="value">{inv.invoice_no}</span></div>
        <div className="row"><span className="label">Date</span><span className="value">{dt}</span></div>
        <hr/>
        <div><b>{inv.customer?.name}</b></div>
        <div>{inv.customer?.phone}</div>
        {inv.customer?.address && <div style={{ fontSize: "10px" }}>{inv.customer.address}</div>}
        {inv.service_request?.package_name && (
          <div style={{ marginTop: "2mm" }}>Package: {inv.service_request.package_name}</div>
        )}
        <hr/>
        {items.map((it, i) => (
          <div key={`item-${i}`}>
            <div>{nameOf(it)}</div>
            <div className="row">
              <span className="label">
                {qtyOf(it) !== "" ? `${qtyOf(it)} × ${fmt(it.rate)}` : ""}
              </span>
              <span className="value">{fmt(it.amount)}</span>
            </div>
          </div>
        ))}
        {totalWaste > 0 && (
          <>
            <hr/>
            <div className="row"><span className="label">Total waste</span><span className="value">{totalWaste.toFixed(2)} kg</span></div>
          </>
        )}
        <hr/>
        <div className="row"><span className="label">Subtotal</span><span className="value">{fmt(inv.subtotal)}</span></div>
        <div className="row"><span className="label">GST ({inv.gst_rate}%)</span><span className="value">{fmt(inv.gst_amount)}</span></div>
        <hr/>
        <div className="row bigtotal"><span>TOTAL</span><span>{fmt(inv.total)}</span></div>
        <hr/>
        <div className="center">
          <b>Status: {(inv.payment_status || "").toUpperCase()}</b>
        </div>
        {inv.ebill_url && (
          <div className="center" style={{ marginTop: "2mm", fontSize: "9px", wordBreak: "break-all" }}>
            e-Bill: {inv.ebill_url}
          </div>
        )}
        <div className="thanks">*** Thank you ***</div>
        <div className="center" style={{ fontSize: "9px" }}>www.rescigo.in</div>
      </div>
    </>
  );
}
